import React, { useState, useEffect } from 'react';

import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { newsApi } from '../services/api';
import { NewsPostCreate, NewsPostUpdate, NewsPriority, NewsStatus } from '../types';

const NewsEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [formData, setFormData] = useState<NewsPostCreate>({
    title: '',
    content: '',
    summary: '',
    image_url: '',
    status: 'draft',
    priority: 'normal',
    is_pinned: false,
    publish_date: undefined,
    expire_date: undefined,
    category_ids: [],
  });

  const [error, setError] = useState<string | null>(null);

  // Fetch existing post if editing
  const { data: post, isLoading: loadingPost } = useQuery({
    queryKey: ['news-post', id],
    queryFn: () => newsApi.getNewsPost(Number(id)),
    enabled: isEdit,
  });

  // Populate form when editing
  useEffect(() => {
    if (post && isEdit) {
      setFormData({
        title: post.title,
        content: post.content,
        summary: post.summary || '',
        image_url: post.image_url || '',
        status: post.status,
        priority: post.priority,
        is_pinned: post.is_pinned,
        publish_date: post.publish_date,
        expire_date: post.expire_date,
        category_ids: [],
      });
    }
  }, [post, isEdit]);

  const createMutation = useMutation({
    mutationFn: (data: NewsPostCreate) => newsApi.createNewsPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-manage'] });
      navigate('/news/manage');
    },
    onError: () => {
      setError('Failed to create news post. Please try again.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: NewsPostUpdate) => newsApi.updateNewsPost(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-manage'] });
      queryClient.invalidateQueries({ queryKey: ['news-post', id] });
      navigate('/news/manage');
    },
    onError: () => {
      setError('Failed to update news post. Please try again.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleSaveAndPublish = () => {
    setError(null);
    const publishData: NewsPostCreate = {
      ...formData,
      status: 'published',
      publish_date: formData.publish_date || new Date().toISOString(),
    };

    if (isEdit) {
      updateMutation.mutate(publishData);
    } else {
      createMutation.mutate(publishData);
    }
  };

  if (loadingPost && isEdit) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/news/manage')}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit News Post' : 'Create News Post'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            {/* Title */}
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              margin="normal"
              inputProps={{ maxLength: 200 }}
              helperText={`${formData.title.length}/200 characters`}
            />

            {/* Summary */}
            <TextField
              fullWidth
              label="Summary"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.summary?.length || 0}/500 characters`}
            />

            {/* Content */}
            <TextField
              fullWidth
              label="Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              margin="normal"
              multiline
              rows={10}
              helperText="Full content of the news post"
            />

            {/* Image URL */}
            <TextField
              fullWidth
              label="Image URL"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              margin="normal"
              helperText="Optional featured image URL"
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              {/* Priority */}
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as NewsPriority })}
                >
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>

              {/* Status */}
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as NewsStatus })}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Pin Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                />
              }
              label="Pin to top"
              sx={{ mt: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              {/* Publish Date */}
              <TextField
                fullWidth
                label="Publish Date"
                type="datetime-local"
                value={formData.publish_date ? new Date(formData.publish_date).toISOString().slice(0, 16) : ''}
                onChange={(e) => setFormData({ ...formData, publish_date: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                InputLabelProps={{ shrink: true }}
                helperText="Optional: Schedule for future publication"
              />

              {/* Expire Date */}
              <TextField
                fullWidth
                label="Expire Date"
                type="datetime-local"
                value={formData.expire_date ? new Date(formData.expire_date).toISOString().slice(0, 16) : ''}
                onChange={(e) => setFormData({ ...formData, expire_date: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                InputLabelProps={{ shrink: true }}
                helperText="Optional: Auto-hide after this date"
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                type="submit"
                variant="outlined"
                startIcon={<SaveIcon />}
                disabled={createMutation.isLoading || updateMutation.isLoading}
              >
                {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save as Draft'}
              </Button>
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={handleSaveAndPublish}
                disabled={createMutation.isLoading || updateMutation.isLoading}
              >
                {createMutation.isLoading || updateMutation.isLoading ? 'Publishing...' : 'Save & Publish'}
              </Button>
            </Box>
          </form>
        </Paper>
        </>
      </Box>
    </Container>
  );
};

export default NewsEditor;
